import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {showLoadingBar, showMessages} from 'app/store/sweet-api/utils';
import {ACM_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {ACM_TAG, KB_TAG} from 'app/utils/rtk-tags';

import {CreateAcmProps} from './types';

import type {
  AcmCertificateSerializer,
  CompletedUserJobSerializedType,
  UserJobSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [ACM_TAG, KB_TAG]})
  .injectEndpoints({
    endpoints: (build) => ({
      acm: build.query<AcmCertificateSerializer, {url: string}>({
        query: ({url}) => ({
          url,
        }),
        providesTags: (response) =>
          response != null
            ? [
                {type: ACM_TAG, id: response.url},
                {type: ACM_TAG, id: 'list'},
              ]
            : [],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
        },
      }),
      createAcm: build.mutation<UserJobSerializedType, CreateAcmProps>({
        queryFn: (
          body,
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: ACM_API_URL,
                    method: 'POST',
                    body,
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [
          {
            type: ACM_TAG,
            id: 'list',
          },
          {
            type: KB_TAG,
            id: 'list',
          },
        ],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: {
              fallback: () => gt('Unable to create validation record.'),
              byStatus: {
                400: (response) =>
                  response && Object.values(response).flat().join(', '),
              },
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
      }),
      deleteAcm: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          url,
          method: 'DELETE',
        }),
        invalidatesTags: (response, error, {url}) => [
          {
            type: ACM_TAG,
            id: 'list',
          },
          {
            type: KB_TAG,
            id: 'list',
          },
          {
            type: ACM_TAG,
            id: url,
          },
        ],
      }),
      startValidation: build.mutation<
        CompletedUserJobSerializedType,
        {url: string}
      >({
        queryFn: (
          {url},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${url}validate/`,
                    method: 'PATCH',
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: (response, error, {url}) => {
          return [
            {
              type: ACM_TAG,
              id: 'list',
            },
            {
              type: ACM_TAG,
              id: url,
            },
          ];
        },
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Unable to start validation.'),
          })(args, api);
        },
      }),
      confirmConfiguration: build.mutation<unknown, {url: string}>({
        queryFn: (
          {url},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${url}confirmation/`,
                    method: 'PATCH',
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: (response, error, {url}) => [
          {
            type: ACM_TAG,
            id: 'list',
          },
          {
            type: ACM_TAG,
            id: url,
          },
        ],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () =>
              gt('There was a problem with the DNS for your knowledge base.'),
          })(args, api);
        },
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useAcmQuery: useAcm,
  useCreateAcmMutation: useCreateAcm,
  useDeleteAcmMutation: useDeleteAcm,
  useStartValidationMutation: useStartValidation,
  useConfirmConfigurationMutation: useConfirmConfiguration,
} = extendedAPI;
